import api_base from './base';
import {
  SuperAdminSystemStats,
  SuperAdminCabinet,
  SuperAdminCabinetStats,
  SuperAdminCabinetCreate,
  SuperAdminCabinetToggle,
  SuperAdminCabinetApproval,
  SuperAdminUser,
  SuperAdminUserResetPassword,
  SuperAdminUserCreate,
  Subscription,
  PaginatedSubscriptionResponse,
  SubscriptionListParams,
  Payment,
  PaymentListParams,
  PaginatedPaymentResponse,
  PaymentUpdate,
  LawyerProfileCreate,
  LawyerProfileDetail,
  LawyerProfileUpdate,
  LawyerProfileListParams,
  LawyerProfileListResponse,
  SendClaimResponse,
  ListingRequestDashboard,
  ApproveListingRequestResponse,
  LawyerProfileApprovalStatus,
  LawyerProfileDetail as LawyerProfileDetailType,
} from '@/types/super-admin';
import type { Plan, PlanListParams, PaginatedPlanResponse } from '@/types/plan';

export const superAdminService = {
  // Dashboard Stats
  getDashboardStats: async (params?: {
    include?: string;
    days?: number;
  }): Promise<SuperAdminSystemStats> => {
    const response = await api_base.get('/api/v1/super-admin/dashboard/', { params });
    return response.data;
  },

  // Cabinet Management
  getCabinets: async (params?: {
    ordering?: string;
    page_size?: number;
    page?: number;
    approval_status?: 'pending' | 'approved' | 'rejected';
    is_active?: boolean;
    name?: string;
  }): Promise<SuperAdminCabinet[]> => {
    const response = await api_base.get('/api/v1/super-admin/cabinets/', { params });
    return response.data;
  },

  getCabinet: async (id: string): Promise<SuperAdminCabinetStats> => {
    const response = await api_base.get(`/api/v1/super-admin/cabinets/${id}/`);
    return response.data;
  },

  createCabinet: async (data: SuperAdminCabinetCreate): Promise<SuperAdminCabinet> => {
    const response = await api_base.post('/api/v1/super-admin/cabinets/create/', data);
    return response.data;
  },

  deleteCabinet: async (id: string): Promise<void> => {
    await api_base.delete(`/api/v1/super-admin/cabinets/${id}/delete/`);
  },

  toggleCabinet: async (id: string, data: SuperAdminCabinetToggle): Promise<SuperAdminCabinet> => {
    const response = await api_base.post(`/api/v1/super-admin/cabinets/${id}/toggle/`, data);
    return response.data;
  },

  // Approve or reject a signup demande (pending cabinet)
  approveCabinet: async (id: string, data: SuperAdminCabinetApproval): Promise<SuperAdminCabinet> => {
    const response = await api_base.post(`/api/v1/super-admin/cabinets/${id}/approval/`, data);
    return response.data;
  },

  getCabinetUsers: async (cabinetId: string, params?: {
    ordering?: string;
    page_size?: number;
    page?: number;
  }): Promise<{
    count: number;
    next?: string;
    previous?: string;
    results: SuperAdminUser[];
  }> => {
    const response = await api_base.get(`/api/v1/super-admin/cabinets/${cabinetId}/users/`, { params });
    return response.data;
  },

  // User Management
  getUsers: async (params?: {
    ordering?: string;
    search?: string;
    page_size?: number;
    page?: number;
    cabinet?: string;
    // Filters
    is_admin?: boolean;
    include_all?: boolean;
  }): Promise<{
    count: number;
    next?: string;
    previous?: string;
    results: SuperAdminUser[];
  }> => {
    const response = await api_base.get('/api/v1/super-admin/users/', { params });
    return response.data;
  },

  getUser: async (id: string): Promise<SuperAdminUser> => {
    const response = await api_base.get(`/api/v1/super-admin/users/${id}/`);
    return response.data;
  },

  createUser: async (data: SuperAdminUserCreate): Promise<SuperAdminUser> => {
    const response = await api_base.post('/api/v1/super-admin/users/create/', data);
    return response.data;
  },

  resetUserPassword: async (id: string, data: SuperAdminUserResetPassword): Promise<void> => {
    await api_base.post(`/api/v1/super-admin/users/${id}/reset-password/`, data);
  },

  toggleUserAdmin: async (id: string): Promise<SuperAdminUser> => {
    const response = await api_base.post(`/api/v1/super-admin/users/${id}/toggle-admin/`);
    return response.data;
  },

  toggleUserStatus: async (id: string): Promise<SuperAdminUser> => {
    const response = await api_base.post(`/api/v1/super-admin/users/${id}/toggle-status/`);
    return response.data;
  },

  // Plans Management (Super Admin)
  getPlans: async (params?: PlanListParams): Promise<PaginatedPlanResponse> => {
    const response = await api_base.get('/api/v1/super-admin/plans/', { params });
    return response.data;
  },

  getPlan: async (id: string): Promise<Plan> => {
    const response = await api_base.get(`/api/v1/super-admin/plans/${id}/`);
    return response.data;
  },

  createPlan: async (data: Plan): Promise<Plan> => {
    const response = await api_base.post('/api/v1/super-admin/plans/create/', data);
    return response.data;
  },

  updatePlan: async (id: string, data: Plan): Promise<Plan> => {
    const response = await api_base.put(`/api/v1/super-admin/plans/${id}/update/`, data);
    return response.data;
  },

  patchPlan: async (id: string, data: Partial<Plan>): Promise<Plan> => {
    const response = await api_base.patch(`/api/v1/super-admin/plans/${id}/update/`, data);
    return response.data;
  },

  deletePlan: async (id: string): Promise<void> => {
    await api_base.delete(`/api/v1/super-admin/plans/${id}/delete/`);
  },

  // Subscriptions Management (Super Admin)
  getSubscriptions: async (params?: SubscriptionListParams): Promise<PaginatedSubscriptionResponse> => {
    const response = await api_base.get('/api/v1/super-admin/subscriptions/', { params });
    return response.data;
  },

  getSubscription: async (id: string): Promise<Subscription> => {
    const response = await api_base.get(`/api/v1/super-admin/subscriptions/${id}/`);
    return response.data;
  },

  // Payments Management (Super Admin)
  getPayments: async (params?: PaymentListParams): Promise<PaginatedPaymentResponse> => {
    const response = await api_base.get('/api/v1/super-admin/payments/', { params });
    return response.data;
  },

  getPayment: async (id: string): Promise<Payment> => {
    const response = await api_base.get(`/api/v1/super-admin/payments/${id}/`);
    return response.data;
  },

  updatePaymentStatus: async (id: string, data: PaymentUpdate): Promise<PaymentUpdate> => {
    const response = await api_base.put(`/api/v1/super-admin/payments/${id}/validate/`, data);
    return response.data;
  },

  validatePayment: async (id: string, data: PaymentUpdate): Promise<Payment> => {
    const response = await api_base.patch(`/api/v1/super-admin/payments/${id}/validate/`, data);
    return response.data;
  },

  // Audit Logs Management (Super Admin)
  getAuditLogs: async (params?: Partial<import('@/types/super-admin').AuditLogsParams>): Promise<import('@/types/super-admin').AuditLogsResponse> => {
    const response = await api_base.get('/api/v1/super-admin/audit-logs/', { params });
    return response.data;
  },

  // Lawyer Profile Management (Super Admin)
  getLawyerProfiles: async (params?: LawyerProfileListParams): Promise<LawyerProfileListResponse> => {
    const response = await api_base.get('/api/v1/super-admin/lawyer-profiles/', { params });
    return response.data;
  },

  createLawyerProfile: async (data: LawyerProfileCreate): Promise<LawyerProfileDetail> => {
    const response = await api_base.post('/api/v1/super-admin/lawyer-profiles/create/', data);
    return response.data;
  },

  getLawyerProfile: async (id: string): Promise<LawyerProfileDetail> => {
    const response = await api_base.get(`/api/v1/super-admin/lawyer-profiles/${id}/`);
    return response.data;
  },

  updateLawyerProfile: async (id: string, data: LawyerProfileUpdate): Promise<LawyerProfileDetail> => {
    const response = await api_base.patch(`/api/v1/super-admin/lawyer-profiles/${id}/`, data);
    return response.data;
  },

  /**
   * Delete a profile. `force` is required by the API for profiles already
   * claimed by a cabinet — without it the request fails with 409.
   */
  deleteLawyerProfile: async (id: string, force = false): Promise<void> => {
    await api_base.delete(`/api/v1/super-admin/lawyer-profiles/${id}/`, {
      params: force ? { force: 'true' } : undefined,
    });
  },

  sendClaimLink: async (id: string): Promise<SendClaimResponse> => {
    const response = await api_base.post(`/api/v1/super-admin/lawyer-profiles/${id}/send-claim/`);
    return response.data;
  },

  // Public listing requests ("référencez-moi") — moderation queue

  /** Counters + the oldest pending requests, for the moderation dashboard. */
  getListingRequestDashboard: async (params?: { days?: number; limit?: number }): Promise<ListingRequestDashboard> => {
    const response = await api_base.get('/api/v1/super-admin/lawyer-profiles/listing-requests/dashboard/', { params });
    return response.data;
  },

  /**
   * Approve a pending request: the profile goes live in the public directory.
   * `sendReviewLink` additionally emails the lawyer their self-service link.
   */
  approveListingRequest: async (id: string, sendReviewLink = false): Promise<ApproveListingRequestResponse> => {
    const response = await api_base.post(
      `/api/v1/super-admin/lawyer-profiles/${id}/approve/`,
      { send_review_link: sendReviewLink },
    );
    return response.data;
  },

  rejectListingRequest: async (id: string, rejectionReason = ''): Promise<LawyerProfileDetailType> => {
    const response = await api_base.post(
      `/api/v1/super-admin/lawyer-profiles/${id}/reject/`,
      { rejection_reason: rejectionReason },
    );
    return response.data;
  },

  /** CSV extract of the requests. Returns a Blob the caller saves to disk. */
  exportListingRequests: async (approvalStatus?: LawyerProfileApprovalStatus): Promise<Blob> => {
    const response = await api_base.get('/api/v1/super-admin/lawyer-profiles/listing-requests/export/', {
      params: approvalStatus ? { approval_status: approvalStatus } : undefined,
      responseType: 'blob',
    });
    return response.data;
  },
};